import { Component, signal } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { ICellRendererAngularComp } from 'ag-grid-angular';
import { ICellRendererParams } from 'ag-grid-community';
import { NgClass } from '@angular/common';
import { RouterModule } from '@angular/router';
import { TsButtonModule } from 'tsui';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { Store } from '@ngxs/store';
import { AccountState } from '@state/account/state';
import { FeatureFlag } from '@core/enums';
import { Observable } from 'rxjs';
import { Dialog } from '@angular/cdk/dialog';
import {
  LinkKeyDateModalComponent,
  LinkKeyDateResult,
} from '@shared/modals/link-key-date-modal/link-key-date-modal.component';
import { TaskState } from '@tasks/state/state';
import { TaskView } from '@tasks/tasks.types';
import { TaskRelatedToModule } from '@tasks/tasks.types';
import { RelatedToType } from '@shared/enums';

@Component({
  standalone: true,
  imports: [
    MatIconModule,
    NgClass,
    RouterModule,
    TsButtonModule,
    NgClass,
    MatProgressBarModule,
    MatTooltipModule,
    TsButtonModule,
    MatMenuModule,
  ],
  selector: 'button-renderer',
  template: `
    <div class="row-group">
      <div class="section-container">
        <div
          class="title-container"
          [class.font-medium]="params.data.group"
          [class.mt-1]="params.data.group"
          [class.text-cool-gray-500]="title() === ''"
        >
          <span (click)="openTaskDetail(params.data)" data-testid="task-table-task-title">
            {{ title() }}
          </span>
          @if (params.data?.children?.length || params.data?.group) {
            <button
              class="expand-icon"
              (click)="onExpandClick()"
              mat-icon-button
              attr.data-testid="task-table-expand-section"
            >
              <mat-icon class="expand-icon" [ngClass]="{ expanded: isExpanded }">
                {{ isExpanded ? 'keyboard_arrow_down' : 'keyboard_arrow_right' }}
              </mat-icon>
            </button>
          }
        </div>

        @if (!params.data?.group) {
          <div class="section-container-actions flex gap-8">
            <div class="section-progress">
              <mat-progress-bar
                color="primary"
                mode="determinate"
                [value]="params.data.percent_complete"
                [matTooltip]="params.data.percent_complete + '% complete'"
              ></mat-progress-bar>
              <span>{{ params.data.num_closed_children }} / {{ params.data.num_children }}</span>
            </div>
            <div>
              <ts-button
                variant="secondary"
                (click)="addTask('task')"
                data-dd-action-name="Tasks - Table - New Task"
                [debounceTime]="250"
                [loading]="isAdding === 'task'"
                data-testid="add-task-button"
                >Add Task</ts-button
              >
            </div>
            <div>
              @if (hasKeyDateLinkingEnabled && isProjectModule) {
                <ts-button
                  variant="secondary"
                  [matMenuTriggerFor]="keyDateMenu"
                  data-dd-action-name="Tasks - Table - Key Date Options"
                  data-testid="key-date-options-dropdown-button"
                  >Add Key Date</ts-button
                >
                <mat-menu #keyDateMenu="matMenu">
                  <button mat-menu-item (click)="addTask('key_date')" data-testid="add-key-date-button">
                    <mat-icon>add</mat-icon>
                    <span>Create Key Date</span>
                  </button>
                  <button mat-menu-item (click)="openLinkKeyDateModal()" data-testid="add-link-key-date-button">
                    <mat-icon>link</mat-icon>
                    <span>Link Key Date</span>
                  </button>
                </mat-menu>
              } @else {
                <ts-button
                  variant="secondary"
                  (click)="addTask('key_date')"
                  data-dd-action-name="Tasks - Table - New Key Date"
                  [debounceTime]="250"
                  [loading]="isAdding === 'key_date'"
                  data-testid="add-key-date-button"
                  >Add Key Date</ts-button
                >
              }
            </div>
          </div>
        }
      </div>
    </div>
  `,
  styles: [
    `
      .section-container {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px;
        padding-top: 4px;
      }

      .section-progress {
        display: flex;
        align-items: center;
        gap: 8px;
        width: 277px;
      }
      .title-container {
        display: flex;
        flex-direction: row;
        gap: 8px;
        align-items: center;
      }
      .tasks-container {
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 4px;
      }
      .task-title {
        text-decoration: none;
        color: inherit;
      }
      .task-title-child {
        margin-left: 20px;
      }
      .task-title-deep-child {
        margin-left: 50px;
      }
      .expand-icon {
        cursor: pointer;
        display: flex;
        align-items: center;
        font-size: 16px;
      }
    `,
  ],
})
export class TableTasksSectionRendererComponent implements ICellRendererAngularComp {
  params;
  isSection = false;
  isParent = false;
  isChild = false;
  isExpanded = false;
  title = signal<string>('');
  isAdding: string = undefined;
  hasKeyDateLinkingEnabled = false;
  isProjectModule = false;

  constructor(
    private store: Store,
    private dialog: Dialog,
  ) {
    this.hasKeyDateLinkingEnabled = this.store.selectSnapshot(
      AccountState.isFeatureEnabled(FeatureFlag.KEY_DATE_LINKING),
    );

    const relatedTo = this.store.selectSnapshot(TaskState.getTaskRelatedTo).relatedToType;
    const taskView = this.store.selectSnapshot(TaskState.getTaskView) as TaskView;
    this.isProjectModule =
      relatedTo === TaskRelatedToModule.Project ? true : taskView.applicable_type === RelatedToType.Project;
  }

  agInit(params: ICellRendererParams): void {
    this.params = params;
    params.registerRowDragger(params.eParentOfValue, undefined, params.data.name, true);
    this.calculatePercentComplete(params.data);
    this.isSection = this.params.data?.task_type === 'section' || false;
    this.isParent = (this.params.data?.task_type === 'task' && this.params.data?.children?.length > 0) || false;
    this.isChild = this.params.data?.parent_id || false;
    this.isExpanded = this.params.node.expanded;
    this.title.set(params.data?.title || params.data?.label || 'Section Title');
  }

  refresh(params: ICellRendererParams): boolean {
    this.params = params;
    this.calculatePercentComplete(params.data);
    this.isExpanded = this.params.node.expanded;
    this.title.set(params.data?.title || params.data?.label || 'Section Title');
    return true;
  }

  onExpandClick() {
    const node = this.params.node;
    const task = this.params.data;
    const context = this.params.context;

    this.isExpanded = !this.isExpanded;
    node.setExpanded(!node.expanded);

    if (task && !task.group) {
      context?.openCloseSection(task);
    }
  }

  openTaskDetail(task: any): void {
    if (task && !task.group) {
      this.params.context.openTaskDetail(task);
    }
  }

  calculatePercentComplete(task: any): void {
    let childTasks = task.children || [];
    let closedChildTasks = childTasks.filter(
      (child) => child.task_type === 'task' && child.workflow_state == 'complete',
    );
    task.num_children = childTasks.filter((t) => t.task_type === 'task').length;
    task.num_closed_children = closedChildTasks.length;
    task.percent_complete = childTasks.length > 0 ? Math.round((closedChildTasks.length / childTasks.length) * 100) : 0;
  }

  addTask(taskType: 'task' | 'key_date', keyDateLinkedTo?: LinkKeyDateResult): void {
    const newTask = { taskType: taskType, parentId: this.params.data.id, rank: this.params.data?.children?.length + 1 };
    if (keyDateLinkedTo) {
      newTask[keyDateLinkedTo.linkType] = keyDateLinkedTo.fieldName;
      newTask['title'] = keyDateLinkedTo.title;
    }

    this.params.context.onNewTask(newTask);
    this.isAdding = taskType;
  }

  openLinkKeyDateModal(): void {
    const dialogRef = this.dialog.open(LinkKeyDateModalComponent, {
      data: {
        taskListId: this.params.data.task_list_id,
      },
      width: '500px',
    });

    dialogRef.closed.subscribe((result: LinkKeyDateResult) => {
      if (result) {
        this.addTask('key_date', result);
      }
    });
  }
}
